'use client'

import { CheckCircleOutline, ReceiptLongOutlined } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useListTaxCodesQuery } from '~/data/api/client/billing'
import { skipWhen } from '~/data/api/hooks/queryOptions'

import type { BillingTaxGuidance } from './types'

/**
 * Verbatim disclaimer from the shared contract (§6). Rendered on every tax guidance card and never
 * dismissable — the backend uses the same wording in its prompt and docs.
 */
export const TAX_GUIDANCE_DISCLAIMER =
  'Tax guidance from Synop Assistant is general information, not tax, legal or accounting advice. ' +
  'Tax treatment depends on your jurisdiction, registrations and customer. Confirm with a qualified ' +
  'tax professional before relying on it.'

type TaxGuidanceCardProps = {
  guidance: BillingTaxGuidance
  /** Writes `taxCode` / `pricing.taxBehavior` into the page form; omitted when the page has no form. */
  onApply?: (guidance: BillingTaxGuidance) => void
  disabled?: boolean
}

export default function TaxGuidanceCard({ guidance, onApply, disabled }: TaxGuidanceCardProps) {
  const t = useTranslations('features.BillingComposer')
  const [applied, setApplied] = useState(false)
  const code = guidance.suggestedTaxCode?.trim()
  const { data: taxCodes } = useListTaxCodesQuery({ query: code, limit: 5 }, skipWhen(!code))
  const codeName = taxCodes?.find((candidate) => candidate.id === code)?.name
  const canApply = Boolean(onApply) && Boolean(code || guidance.suggestedTaxBehavior)

  const apply = () => {
    onApply?.(guidance)
    setApplied(true)
  }

  return (
    <Card variant="outlined" data-cy="tax-guidance-card">
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ReceiptLongOutlined fontSize="small" color="action" />
            <Typography variant="subtitle2">{t('tax_title')}</Typography>
          </Stack>

          {(code || guidance.suggestedTaxBehavior) && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {code && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={codeName ? `${code} · ${codeName}` : code}
                  title={codeName}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
              {guidance.suggestedTaxBehavior && (
                <Chip size="small" variant="outlined" label={t(`tax_behavior_${guidance.suggestedTaxBehavior}`)} />
              )}
            </Stack>
          )}

          {guidance.rationale && <Typography variant="body2">{guidance.rationale}</Typography>}

          {guidance.considerations.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {t('tax_considerations')}
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
                {guidance.considerations.map((consideration) => (
                  <Typography key={consideration} component="li" variant="body2">
                    {consideration}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {guidance.sources && guidance.sources.length > 0 && (
            <Typography variant="caption" color="text.disabled">
              {t('tax_sources', { sources: guidance.sources.join(', ') })}
            </Typography>
          )}

          <Alert severity="warning" data-cy="tax-guidance-disclaimer">
            {TAX_GUIDANCE_DISCLAIMER}
          </Alert>

          {canApply && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={apply} disabled={disabled || applied}>
                {applied ? t('tax_applied') : t('tax_apply')}
              </Button>
              {applied && (
                <Stack direction="row" spacing={0.5} alignItems="center" color="success.main">
                  <CheckCircleOutline fontSize="inherit" />
                  <Typography variant="caption" color="success.main">
                    {t('draft_reviewHint')}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
